import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase as typedSupabase } from '../lib/supabase';
import { log } from '../lib/logger';

// invoice_requests / app_notifications and their RPCs are newer than the
// generated database types
const supabase = typedSupabase as unknown as SupabaseClient;

export interface InvoiceRequestLine {
  id: string;
  amount: number;
  fulfilled_at?: string | null;
  invoice_number?: string | null;
  issued_date?: string | null;
  due_date?: string | null;
  purchase_order?: string | null;
  net_amount?: number | null;
  total_amount?: number | null;
}

export interface InvoiceRequest {
  id: string;
  campaign_id: string;
  campaign_name: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  client_name: string | null;
  client_vat: string | null;
  client_address: string | null;
  purchase_order: string | null;
  notes: string | null;
  lines: InvoiceRequestLine[];
  total: number;
  status: 'pending' | 'done' | 'cancelled';
  created_at: string;
  resolved_at: string | null;
}

export interface NewInvoiceRequest {
  campaign_id: string;
  campaign_name: string;
  client_name: string | null;
  client_vat: string | null;
  client_address: string | null;
  purchase_order: string | null;
  notes: string | null;
  amounts: number[];
}

/** What the PDF scan (or the person) filled in for one requested invoice. */
export interface FulfilledInvoice {
  invoice_number: string;
  issued_date: string;
  due_date: string;
  purchase_order: string;
  net_amount: number | null;
  total_amount: number | null;
}

function normalize(row: InvoiceRequest): InvoiceRequest {
  return {
    ...row,
    total: Number(row.total) || 0,
    lines: (Array.isArray(row.lines) ? row.lines : []).map(l => ({ ...l, amount: Number(l.amount) || 0 })),
  };
}

export const pendingLines = (request: InvoiceRequest) => request.lines.filter(l => !l.fulfilled_at);

export const invoiceRequestService = {
  async listByCampaign(campaignId: string): Promise<InvoiceRequest[]> {
    const { data, error } = await supabase
      .from('invoice_requests')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []).map(normalize);
  },

  async listPending(): Promise<InvoiceRequest[]> {
    const { data, error } = await supabase
      .from('invoice_requests')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(normalize);
  },

  async listByIds(ids: string[]): Promise<InvoiceRequest[]> {
    if (ids.length === 0) return [];
    const { data, error } = await supabase
      .from('invoice_requests')
      .select('*')
      .in('id', ids);
    if (error) throw error;
    return (data || []).map(normalize);
  },

  async create(input: NewInvoiceRequest): Promise<InvoiceRequest> {
    const { data, error } = await supabase
      .from('invoice_requests')
      .insert({
        campaign_id: input.campaign_id,
        campaign_name: input.campaign_name,
        client_name: input.client_name,
        client_vat: input.client_vat,
        client_address: input.client_address,
        purchase_order: input.purchase_order,
        notes: input.notes,
        lines: input.amounts.map(amount => ({ amount })),
      })
      .select('*')
      .single();
    if (error) throw error;
    const request = normalize(data as InvoiceRequest);

    // The bell notification is written by the database; the email is a bonus
    supabase.functions
      .invoke('invoice-request-email', { body: { request_id: request.id } })
      .catch(err => log.warn('Invoice request email not sent:', err));

    return request;
  },

  async fulfilLine(requestId: string, lineId: string, invoice: FulfilledInvoice): Promise<InvoiceRequest> {
    const { data, error } = await supabase.rpc('fulfil_invoice_request_line', {
      p_request_id: requestId,
      p_line_id: lineId,
      p_invoice: invoice,
    });
    if (error) throw error;
    return normalize(data as InvoiceRequest);
  },

  async cancel(requestId: string): Promise<InvoiceRequest> {
    const { data, error } = await supabase.rpc('cancel_invoice_request', { p_request_id: requestId });
    if (error) throw error;
    return normalize(data as InvoiceRequest);
  },
};

// ── Notifications (main app bell) ────────────────────────────────────────────

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  campaign_id: string | null;
  invoice_request_id: string | null;
  read_at: string | null;
  created_at: string;
}

export const appNotificationService = {
  async list(limit = 30): Promise<AppNotification[]> {
    const { data, error } = await supabase
      .from('app_notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as AppNotification[];
  },

  async markRead(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('app_notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids)
      .is('read_at', null);
    if (error) throw error;
  },
};
